import { XMarkIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import { DemandExample } from '../../services/types/demand-example-types';
import { Button } from '../ui/catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../ui/catalyst/dialog';
import { Text } from '../ui/catalyst/text';

interface FilePreviewModalProps {
  isOpen: boolean;
  example: DemandExample | null;
  onClose: () => void;
}

export const FilePreviewModal = ({
  isOpen,
  example,
  onClose,
}: FilePreviewModalProps) => {
  const [loadError, setLoadError] = useState(false);

  if (!example || !example.demand) {
    return null;
  }

  const handleIframeError = () => {
    setLoadError(true);
  };

  const openInNewTab = () => {
    if (example.demand) {
      window.open(example.demand, '_blank');
    }
  };

  const downloadFile = () => {
    if (example.demand) {
      const link = document.createElement('a');
      link.href = example.demand;
      link.download = `${example.title || 'demand-example'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} size="5xl">
      <DialogTitle className="flex items-center justify-between">
        <div>
          <span>Preview: {example.title || 'Untitled'}</span>
          {example.matter_type && (
            <span className="ml-2 text-sm text-zinc-500">
              ({example.matter_type})
            </span>
          )}
        </div>
        <Button plain onClick={onClose}>
          <XMarkIcon className="h-5 w-5" />
        </Button>
      </DialogTitle>

      <DialogBody className="p-0">
        {loadError ? (
          <div className="flex flex-col items-center justify-center h-96 bg-zinc-50 rounded-lg">
            <Text className="text-zinc-600 mb-4">
              Unable to preview this PDF file
            </Text>
            <div className="flex gap-2">
              <Button onClick={openInNewTab}>Open in New Tab</Button>
              <Button outline onClick={downloadFile}>
                Download File
              </Button>
            </div>
          </div>
        ) : (
          <iframe
            src={example.demand}
            className="w-full h-[80vh] border-0 rounded-lg"
            title={`Preview of ${example.title || 'demand example'}`}
            onError={handleIframeError}
          />
        )}
      </DialogBody>

      <DialogActions>
        <Button outline onClick={downloadFile}>
          Download
        </Button>
        <Button outline onClick={openInNewTab}>
          Open in New Tab
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
